import { useAnimals, useLots, useRegisterBatchEvent } from '@/api/hooks';
import { DairySubnav } from '@/components/dairy/DairySubnav';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { DateInput } from '@/components/ui/DateInput';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { getErrorMessage } from '@/lib/api';
import { MILKING_SHIFT_OPTIONS } from '@/lib/dairy';
import { todayIso } from '@/lib/format';
import { useEffect, useMemo, useState } from 'react';

type Row = { animalId: number; label: string; liters: string; included: boolean };

export function DairyBatchPage() {
  const { propertyId } = usePropertyScope();

  const [lotId, setLotId] = useState('');
  const [eventDate, setEventDate] = useState(todayIso);
  const [shift, setShift] = useState('morning');
  const [responsible, setResponsible] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: lots } = useLots(propertyId);
  const numericLotId = lotId ? Number(lotId) : undefined;
  const { data: animals, isLoading } = useAnimals({
    property_id: propertyId ?? undefined,
    lot_id: numericLotId,
    sex: 'female',
    per_page: 500,
  });
  const registerBatch = useRegisterBatchEvent();

  useEffect(() => {
    if (!animals?.animals) {
      setRows([]);
      return;
    }
    setRows((prev) => {
      const byId = new Map(prev.map((r) => [r.animalId, r]));
      return animals.animals.map((a) => {
        const existing = byId.get(a.id);
        return {
          animalId: a.id,
          label: `${a.internal_number}${a.animal_name ? ` — ${a.animal_name}` : ''}`,
          liters: existing?.liters ?? '',
          included: existing?.included ?? false,
        };
      });
    });
  }, [animals?.animals]);

  const selected = useMemo(() => rows.filter((r) => r.included && r.liters.trim() !== ''), [rows]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!propertyId || !numericLotId) {
      setError('Selecione um lote.');
      return;
    }
    const items = selected
      .map((r) => ({ animal_id: r.animalId, production_liters: Number(r.liters) }))
      .filter((r) => !Number.isNaN(r.production_liters) && r.production_liters >= 0);
    if (items.length === 0) {
      setError('Marque animais e informe os litros.');
      return;
    }
    try {
      const result = await registerBatch.mutateAsync({
        event_type: 'milk_weighing',
        property_id: propertyId,
        event_date: eventDate,
        lot_id: numericLotId,
        milking_shift: shift,
        responsible_name: responsible || null,
        notes: notes || null,
        items,
      });
      setSuccess(result.message);
      setRows((list) => list.map((r) => ({ ...r, liters: '', included: false })));
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao registrar em lote.'));
    }
  }

  const lotOptions = (lots?.lots ?? [])
    .filter((l) => l.is_active)
    .map((l) => ({ value: l.id, label: l.name }));

  return (
    <div>
      <PageHeader
        title="Pesagem de leite em lote"
        subtitle="Mesmo turno e data para várias vacas do lote"
      />
      <DairySubnav />

      {!propertyId ? (
        <Card>
          <CardBody className="py-10 text-center text-manejai-bark/60">Selecione uma propriedade.</CardBody>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert variant="error">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Card>
            <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Select
                label="Lote"
                required
                value={lotId}
                onChange={(e) => setLotId(e.target.value)}
                placeholder="Selecione…"
                options={lotOptions}
              />
              <DateInput label="Data" required value={eventDate} onChange={setEventDate} />
              <Select
                label="Turno"
                required
                value={shift}
                onChange={(e) => setShift(e.target.value)}
                options={MILKING_SHIFT_OPTIONS}
              />
              <Input
                label="Ordenhador"
                value={responsible}
                onChange={(e) => setResponsible(e.target.value)}
              />
              <div className="sm:col-span-2 lg:col-span-4">
                <Input
                  label="Observações"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </CardBody>
          </Card>

          {!lotId ? (
            <Card>
              <CardBody className="py-10 text-center text-manejai-bark/55">Escolha um lote.</CardBody>
            </Card>
          ) : isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 rounded-xl bg-manejai-sand/40 animate-pulse" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <Card>
              <CardBody className="py-10 text-center text-manejai-bark/55">
                Nenhuma fêmea ativa neste lote.
              </CardBody>
            </Card>
          ) : (
            <>
              <p className="text-sm text-manejai-bark/65">
                {rows.length} vacas · {selected.length} com produção
              </p>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/55">
                        <th className="px-3 py-3 w-14">Incluir</th>
                        <th className="px-3 py-3">Animal</th>
                        <th className="px-3 py-3 w-36">Litros</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-manejai-sand/50">
                      {rows.map((row, index) => (
                        <tr key={row.animalId}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={row.included}
                              onChange={() =>
                                setRows((list) =>
                                  list.map((r, i) =>
                                    i === index ? { ...r, included: !r.included } : r,
                                  ),
                                )
                              }
                            />
                          </td>
                          <td className="px-3 py-2">{row.label}</td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              className="w-full rounded-lg border border-manejai-sand px-2 py-1.5"
                              value={row.liters}
                              onChange={(e) =>
                                setRows((list) =>
                                  list.map((r, i) =>
                                    i === index
                                      ? { ...r, liters: e.target.value, included: true }
                                      : r,
                                  ),
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <Button type="submit" loading={registerBatch.isPending}>
                Registrar {selected.length || ''} pesagem(ns)
              </Button>
            </>
          )}
        </form>
      )}
    </div>
  );
}
